import { isValidCountryLanguage } from "@/i18n/config"
import { localizedPath } from "@/lib/localized-route-slugs"

export const MARKDOWN_INTERNAL_PREFIX = "/markdown-mirror"
export const MARKDOWN_SOURCE_HEADER = "x-ahead-markdown-source"

function normalizePathname(pathname: string): string {
  if (!pathname) return "/"
  const trimmed = pathname.replace(/\/+$/, "")
  return trimmed || "/"
}

function splitPath(pathname: string): string[] {
  return normalizePathname(pathname).split("/").filter(Boolean)
}

function getLocalizedRouteParts(
  pathname: string
): { country: string; locale: string; rest: string[] } | null {
  const parts = splitPath(pathname)
  if (parts.length < 2) return null
  const [country, locale, ...rest] = parts
  if (!isValidCountryLanguage(country, locale)) return null
  return { country, locale, rest }
}

export function isInternalMarkdownPath(pathname: string): boolean {
  return normalizePathname(pathname).startsWith(MARKDOWN_INTERNAL_PREFIX)
}

export function isMarkdownPreferred(acceptHeader: string | null): boolean {
  return (acceptHeader || "").toLowerCase().includes("text/markdown")
}

export function stripMarkdownSuffix(pathname: string): string {
  const normalized = normalizePathname(pathname)
  if (normalized.endsWith("/index.html.md")) {
    return normalized.slice(0, -"/index.html.md".length) || "/"
  }
  if (normalized.endsWith(".md")) {
    return normalized.slice(0, -".md".length) || "/"
  }
  return normalized
}

export function markdownPathname(pathname: string): string {
  const normalized = normalizePathname(pathname)
  if (normalized === "/") return "/index.html.md"
  if (normalized.endsWith(".md")) return normalized
  return `${normalized}.md`
}

export function markdownAbsoluteUrl(
  baseUrl: string,
  country: string,
  locale: string,
  path = ""
): string {
  const normalizedBase = baseUrl.replace(/\/$/, "")
  // Translated URL where the page has one (lib/localized-route-slugs.ts), so mirror
  // links and llms.txt never point at a redirect.
  const normalizedPath = path
    ? localizedPath(`/${path.replace(/^\/+/, "")}`, locale)
    : ""
  return `${normalizedBase}${markdownPathname(`/${country}/${locale}${normalizedPath}`)}`
}

export function markdownInternalPathFromPublicPath(
  pathname: string
): string | null {
  const publicPath = stripMarkdownSuffix(pathname)
  if (isInternalMarkdownPath(publicPath)) {
    return null
  }

  const localized = getLocalizedRouteParts(publicPath)
  if (!localized) return null
  const { country, locale, rest } = localized
  const suffix = rest.length ? `/${rest.join("/")}` : ""
  return `${MARKDOWN_INTERNAL_PREFIX}/${country}/${locale}${suffix}`
}

/**
 * The mirror to serve for a request that asked for markdown through `Accept`
 * rather than a `.md` URL, or null to serve the path as it is. A path naming
 * a file (`/ch/en/llms.txt`, `/ch/en/llms-full.txt`) is not a page and has no
 * mirror: rewriting it 404'd every fetcher that prefers markdown, including
 * the site's own WebMCP tools.
 */
export function negotiatedMarkdownInternalPath(
  pathname: string
): string | null {
  const last = splitPath(pathname).at(-1) ?? ""
  if (/\.[a-z0-9]+$/i.test(last)) return null
  return markdownInternalPathFromPublicPath(pathname)
}

export function externalMarkdownPathname(pathname: string): string | null {
  const publicPath = stripMarkdownSuffix(pathname)
  if (isInternalMarkdownPath(publicPath)) {
    return null
  }
  if (!getLocalizedRouteParts(publicPath)) return null
  return markdownPathname(publicPath)
}
